import React, { useState, useEffect } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { register } from "../api/auth";
import Particles from 'react-tsparticles';
import { loadSlim } from "tsparticles-slim";

import {
    Container, Box, Typography, TextField, Button, Alert, Paper,
    Stack, CircularProgress, InputAdornment, Fade, Link as MuiLink, Avatar,
    IconButton, Tooltip, Stepper, Step, StepLabel, useMediaQuery, Divider
} from "@mui/material";
import { useTheme } from '@mui/material/styles';

// Icons for the form and decorative panel
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import BarChartIcon from '@mui/icons-material/BarChart';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RecyclingIcon from '@mui/icons-material/Recycling';
import NatureIcon from '@mui/icons-material/Nature';
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf';

export default function RegisterPage() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const [values, setValues] = useState({
        displayName: "",
        email: "",
        password: "",
    });
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [animate, setAnimate] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [passwordChecks, setPasswordChecks] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        digit: false,
        special: false
    });

    // Animation effect on mount
    useEffect(() => {
        const timer = setTimeout(() => {
            setAnimate(true);
        }, 300);
        return () => clearTimeout(timer);
    }, []);

    // Enhanced particle configuration with glowing effect
    const particlesConfig = {
        particles: {
            number: { value: 30, density: { enable: true, value_area: 800 } },
            color: { value: ["#43a047", "#66bb6a", "#81c784", "#ffc400"] },
            shape: { type: "circle" },
            opacity: { value: 0.6, random: true },
            size: { value: 4, random: true, anim: { enable: true, speed: 2 } },
            move: {
                enable: true,
                speed: 1.2,
                direction: "none",
                random: true,
                straight: false,
                outModes: "out",
                attract: { enable: true, rotateX: 600, rotateY: 1200 }
            },
            links: {
                enable: true,
                distance: 150,
                color: "#43a047",
                opacity: 0.2,
                width: 1
            },
            life: {
                duration: { value: 3 },
                count: 0
            }
        },
        interactivity: {
            events: {
                onHover: { enable: true, mode: "bubble" },
                onClick: { enable: true, mode: "push" }
            },
            modes: {
                bubble: {
                    distance: 200,
                    size: 6,
                    duration: 0.4
                },
                push: { quantity: 3 }
            }
        },
        background: { color: { value: "transparent" } }
    };

    const particlesInit = async (engine) => {
        await loadSlim(engine);
    };

    // Animated decorative icons
    const decorativeIcons = [
        { icon: <RecyclingIcon />, color: '#2196f3', size: 50, top: '15%', left: '10%', delay: 0 },
        { icon: <NatureIcon />, color: '#4caf50', size: 40, top: '35%', right: '15%', delay: 0.5 },
        { icon: <EnergySavingsLeafIcon />, color: '#ffc400', size: 45, top: '65%', left: '20%', delay: 1 },
    ];

    // Password validation function
    const validatePassword = (password) => {
        const checks = {
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            digit: /[0-9]/.test(password),
            special: /[^A-Za-z0-9\s]/.test(password) // Any special character except spaces
        };

        setPasswordChecks(checks);

        // Calculate strength score (0-100)
        const strength = Object.values(checks).filter(Boolean).length * 20;
        setPasswordStrength(strength);

        return strength;
    };

    // Check if password meets all requirements
    const isPasswordValid = () => {
        return Object.values(passwordChecks).every(check => check);
    };

    // Check if form is valid
    const isFormValid = () => {
        return values.displayName.trim().length >= 2 &&
               values.email.includes('@') &&
               values.email.includes('.') &&
               isPasswordValid();
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setValues({ ...values, [name]: value });

        if (name === 'password') {
            validatePassword(value);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        setSuccess("");

        // Validate form before submission
        if (!isFormValid()) {
            setError("Please ensure all fields are correctly filled and password meets all requirements.");
            setLoading(false);
            return;
        }

        try {
            await register(values);
            setSuccess("Registration successful! 🌱 Please verify your email. 🌱 Redirecting to login...");
            setTimeout(() => navigate("/login"), 3500);
        } catch (err) {
            // Handle different types of errors
            if (err?.response?.data) {
                if (typeof err.response.data === 'object') {
                    // Handle validation errors
                    const errorMessages = Object.entries(err.response.data)
                        .map(([field, message]) => `${field}: ${message}`)
                        .join(', ');
                    setError(errorMessages);
                } else if (err.response.data.message) {
                    setError(err.response.data.message);
                } else {
                    setError(err.response.data);
                }
            } else {
                setError("Registration failed. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    // Get strength color for password indicator
    const getStrengthColor = () => {
        if (passwordStrength <= 20) return '#f44336';
        if (passwordStrength <= 60) return '#ff9800';
        return '#4caf50';
    };

    return (
        <Box sx={{
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, rgba(248, 250, 248, 0.95) 0%, rgba(232, 244, 234, 0.95) 100%)',
            minHeight: 'calc(100vh - 128px)',
            display: 'flex',
            flexDirection: 'column'
        }}>
            <Particles
                id="tsparticles"
                init={particlesInit}
                options={particlesConfig}
                style={{ position: 'absolute', width: '100%', height: '100%', zIndex: 0 }}
            />

            {/* Decorative floating icons for visual interest */}
            {!isMobile && decorativeIcons.map((item, index) => (
                <Avatar
                    key={index}
                    sx={{
                        position: 'absolute',
                        backgroundColor: item.color,
                        width: item.size,
                        height: item.size,
                        top: item.top,
                        left: item.left,
                        right: item.right,
                        zIndex: 1,
                        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)',
                        animation: `float-${index} ${4 + (index * 0.5)}s ease-in-out infinite alternate`,
                        '@keyframes float-0': {
                            '0%': { transform: 'translateY(0px) rotate(0deg)' },
                            '100%': { transform: 'translateY(-20px) rotate(10deg)' }
                        },
                        '@keyframes float-1': {
                            '0%': { transform: 'translateY(0px) rotate(0deg)' },
                            '100%': { transform: 'translateY(-15px) rotate(-8deg)' }
                        },
                        '@keyframes float-2': {
                            '0%': { transform: 'translateY(0px) rotate(0deg)' },
                            '100%': { transform: 'translateY(-25px) rotate(5deg)' }
                        }
                    }}
                >
                    {item.icon}
                </Avatar>
            ))}

            <Container
                maxWidth="lg"
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    minHeight: '100vh',
                    py: { xs: 4, md: 8 }
                }}
            >
                {/* Left side - Benefits of Joining */}
                <Fade in={animate} timeout={1000} style={{ transitionDelay: '100ms' }}>
                    <Box sx={{
                        maxWidth: 450,
                        mb: { xs: 5, md: 0 },
                        display: { xs: 'none', md: 'block' },
                        p: 4,
                        borderRadius: 4,
                        backdropFilter: 'blur(12px)',
                        backgroundColor: 'rgba(255, 255, 255, 0.25)',
                        border: '1px solid rgba(255, 255, 255, 0.4)',
                        boxShadow: '0 10px 40px rgba(31, 38, 135, 0.15)',
                        transform: animate ? 'translateX(0)' : 'translateX(-50px)',
                        transition: 'transform 0.8s ease-out',
                    }}>
                        {/* Badge */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: -15,
                                left: 20,
                                backgroundColor: '#ffc400',
                                color: '#212121',
                                px: 2,
                                py: 0.5,
                                borderRadius: 4,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                boxShadow: '0 4px 12px rgba(255, 196, 0, 0.3)',
                                animation: 'pulse 2s infinite',
                                '@keyframes pulse': {
                                    '0%': { transform: 'scale(1)' },
                                    '50%': { transform: 'scale(1.05)' },
                                    '100%': { transform: 'scale(1)' }
                                }
                            }}
                        >
                            <EnergySavingsLeafIcon sx={{ fontSize: 16 }} /> Join Our Movement
                        </Box>

                        <Typography
                            variant="h3"
                            fontWeight={700}
                            gutterBottom
                            sx={{
                                backgroundImage: 'linear-gradient(90deg, #1b5e20, #43a047, #66bb6a)',
                                backgroundSize: '200% auto',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                animation: 'gradient 3s ease infinite',
                                '@keyframes gradient': {
                                    '0%': { backgroundPosition: '0% center' },
                                    '50%': { backgroundPosition: '100% center' },
                                    '100%': { backgroundPosition: '0% center' }
                                }
                            }}
                        >
                            Make Every Scan Count
                        </Typography>

                        <Typography variant="h5" color="primary.dark" fontWeight={600} sx={{ mb: 4 }}>
                            Start your eco journey today!
                        </Typography>

                        {/* Benefits with enhanced styling */}
                        {[
                            {
                                icon: <BarChartIcon />,
                                title: "Personal Impact Dashboard",
                                text: "Track your recycling habits and see your environmental impact grow over time"
                            },
                            {
                                icon: <EmojiEventsIcon />,
                                title: "Achievement System",
                                text: "Earn badges, climb leaderboards, and get recognition for your eco-friendly actions"
                            },
                            {
                                icon: <LightbulbIcon />,
                                title: "Smart Recommendations",
                                text: "Receive personalized tips to improve your waste management practices"
                            }
                        ].map((item, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    mb: 3.5,
                                    opacity: animate ? 1 : 0,
                                    transform: animate ? 'translateY(0)' : 'translateY(20px)',
                                    transition: `all 0.6s ease-out ${0.4 + (index * 0.2)}s`,
                                }}
                            >
                                <Avatar
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        mr: 2,
                                        boxShadow: '0 4px 10px rgba(67, 160, 71, 0.2)'
                                    }}
                                >
                                    {item.icon}
                                </Avatar>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                        {item.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {item.text}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}

                        {/* Social proof */}
                        <Box
                            sx={{
                                mt: 4,
                                p: 2.5,
                                borderRadius: 2,
                                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                border: '1px solid rgba(255, 255, 255, 0.8)',
                                transform: animate ? 'translateY(0)' : 'translateY(30px)',
                                opacity: animate ? 1 : 0,
                                transition: 'all 0.8s ease-out 1.2s',
                            }}
                        >
                            <Typography variant="subtitle2" gutterBottom fontWeight={500}>
                                Join our community of
                            </Typography>
                            <Typography
                                variant="h4"
                                color="primary.dark"
                                fontWeight={800}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                10,000+ <Typography component="span" variant="body2" sx={{ fontWeight: 500 }}>eco-warriors worldwide</Typography>
                            </Typography>
                        </Box>
                    </Box>
                </Fade>

                {/* Right side - Registration Form */}
                <Fade in={animate} timeout={1200} style={{ transitionDelay: '300ms' }}>
                    <Paper
                        elevation={4}
                        sx={{
                            p: { xs: 3, sm: 4 },
                            maxWidth: 460,
                            width: '100%',
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: '24px',
                            boxShadow: '0 15px 50px rgba(67, 160, 71, 0.2)',
                            transform: animate ? 'translateY(0)' : 'translateY(30px)',
                            transition: 'transform 0.6s ease-out',
                        }}
                    >
                        <Stack alignItems="center" spacing={2}>
                            <Avatar
                                sx={{
                                    mb: 1,
                                    bgcolor: 'primary.main',
                                    width: 64,
                                    height: 64,
                                    boxShadow: '0 6px 16px rgba(67, 160, 71, 0.25)',
                                    animation: 'pulse-subtle 2s infinite',
                                    '@keyframes pulse-subtle': {
                                        '0%': { boxShadow: '0 6px 16px rgba(67, 160, 71, 0.25)' },
                                        '50%': { boxShadow: '0 6px 22px rgba(67, 160, 71, 0.4)' },
                                        '100%': { boxShadow: '0 6px 16px rgba(67, 160, 71, 0.25)' }
                                    }
                                }}
                            >
                                <PersonAddAlt1Icon sx={{ fontSize: 36 }} />
                            </Avatar>

                            <Typography
                                component="h1"
                                variant="h4"
                                fontWeight={700}
                                color="text.primary"
                                sx={{
                                    position: 'relative',
                                    '&:after': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: -8,
                                        left: '50%',
                                        width: '40px',
                                        height: '3px',
                                        backgroundColor: 'primary.main',
                                        transform: 'translateX(-50%)'
                                    }
                                }}
                            >
                                Create Account
                            </Typography>

                            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 1 }}>
                                Join Sortify and start making a positive impact today!
                            </Typography>

                            {error && (
                                <Alert
                                    severity="error"
                                    sx={{
                                        width: '100%',
                                        animation: 'shake 0.5s',
                                        '@keyframes shake': {
                                            '0%, 100%': { transform: 'translateX(0)' },
                                            '10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-5px)' },
                                            '20%, 40%, 60%, 80%': { transform: 'translateX(5px)' }
                                        }
                                    }}
                                >
                                    {error}
                                </Alert>
                            )}

                            {success && (
                                <Alert
                                    severity="success"
                                    sx={{
                                        width: '100%',
                                        animation: 'expand 0.5s',
                                        '@keyframes expand': {
                                            '0%': { transform: 'scale(0.9)' },
                                            '50%': { transform: 'scale(1.03)' },
                                            '100%': { transform: 'scale(1)' }
                                        }
                                    }}
                                >
                                    {success}
                                </Alert>
                            )}

                            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', mt: 1 }}>
                                <Stack spacing={2.5}>
                                    <TextField
                                        fullWidth
                                        label="Full Name"
                                        name="displayName"
                                        value={values.displayName}
                                        onChange={handleChange}
                                        required
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PersonOutlineIcon sx={{ color: 'primary.main' }} />
                                                </InputAdornment>
                                            )
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'primary.main',
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                    />

                                    <TextField
                                        fullWidth
                                        label="Email Address"
                                        name="email"
                                        type="email"
                                        value={values.email}
                                        onChange={handleChange}
                                        required
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <EmailOutlinedIcon sx={{ color: 'primary.main' }} />
                                                </InputAdornment>
                                            )
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '&.Mui-focused fieldset': {
                                                    borderColor: 'primary.main',
                                                    borderWidth: '2px'
                                                }
                                            }
                                        }}
                                    />

                                    <Box sx={{ width: '100%' }}>
                                        <TextField
                                            fullWidth
                                            label="Password"
                                            name="password"
                                            type={showPassword ? "text" : "password"}
                                            value={values.password}
                                            onChange={handleChange}
                                            required
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <LockOutlinedIcon sx={{ color: 'primary.main' }} />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={() => setShowPassword(!showPassword)}
                                                            edge="end"
                                                        >
                                                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: 'primary.main',
                                                        borderWidth: '2px'
                                                    }
                                                }
                                            }}
                                        />

                                        {/* Password strength indicator */}
                                        {values.password && (
                                            <Box sx={{ mt: 1.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                    <Typography variant="caption" sx={{ mr: 1 }}>
                                                        Password Strength:
                                                    </Typography>
                                                    <Box sx={{ flexGrow: 1, bgcolor: 'rgba(0, 0, 0, 0.08)', borderRadius: 5, height: 6, position: 'relative' }}>
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                left: 0,
                                                                top: 0,
                                                                height: '100%',
                                                                borderRadius: 5,
                                                                width: `${passwordStrength}%`,
                                                                bgcolor: getStrengthColor(),
                                                                transition: 'width 0.3s, background-color 0.3s'
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Password criteria checklist */}
                                                <Box sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '1fr 1fr',
                                                    columnGap: 2,
                                                    rowGap: 0.5,
                                                    mt: 1
                                                }}>
                                                    {Object.entries(passwordChecks).map(([key, value]) => (
                                                        <Box
                                                            key={key}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                color: value ? 'success.main' : 'text.disabled',
                                                                transition: 'color 0.3s'
                                                            }}
                                                        >
                                                            <CheckCircleOutlineIcon
                                                                sx={{
                                                                    fontSize: 14,
                                                                    mr: 0.5,
                                                                    opacity: value ? 1 : 0.5
                                                                }}
                                                            />
                                                            <Typography variant="caption">
                                                                {key === 'length' ? '8+ Characters' :
                                                                    key === 'uppercase' ? 'Uppercase' :
                                                                        key === 'lowercase' ? 'Lowercase' :
                                                                            key === 'digit' ? 'Number' : 'Special Char'}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>

                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        size="large"
                                        disabled={loading}
                                        endIcon={<ArrowForwardIcon />}
                                        sx={{
                                            py: 1.5,
                                            mt: 1.5,
                                            background: 'linear-gradient(90deg, #43a047 30%, #66bb6a 90%)',
                                            boxShadow: '0 4px 12px rgba(67, 160, 71, 0.3)',
                                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-3px)',
                                                boxShadow: '0 6px 15px rgba(67, 160, 71, 0.4)',
                                            }
                                        }}
                                    >
                                        {loading ? <CircularProgress size={26} color="inherit" /> : 'Create My Account'}
                                    </Button>
                                </Stack>
                            </Box>

                            <Divider sx={{ width: '100%', my: 2, opacity: 0.6 }} />

                            <MuiLink
                                component={RouterLink}
                                to="/login"
                                variant="body2"
                                sx={{
                                    color: 'primary.main',
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    '&:hover': {
                                        textDecoration: 'none'
                                    }
                                }}
                            >
                                Already have an account? <Typography component="span" color="primary.dark" fontWeight={700} sx={{ ml: 0.5 }}>Log In</Typography>
                            </MuiLink>
                        </Stack>
                    </Paper>
                </Fade>
            </Container>
        </Box>
    );
}